import React, { useEffect, useState } from 'react';
import { IUser } from '../../models';
import PaneHeadline from '../shared/paneHeadline';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';

import './orderExclusionsAdmin.scss';
import { IOrderExclusion } from '../../models/OrderExclusions.model';
import OrderExclusionsGrid from './OrderExclusionsGird';
import OrderExclusionModal from './OrderExclusionModal';
import OrderExclusionService from '../../services/orderExclusionService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';
import { Spinner2 } from '../shared/spinner2';

export interface IOrderExclusionsAdminProps {
  impersonatedUser: any;
  authentication: IUser;
  exclusions: IOrderExclusion[];
  isLoading: boolean;
  getExclusions: (customerId: number, ignoreCache: boolean) => void;
}

export default function OrderExclusionsAdmin(
  props: IOrderExclusionsAdminProps
): JSX.Element {
  const customerId = props.authentication.impersonatedCustomerId
    ? props.authentication.impersonatedCustomerId
    : props.authentication.customerId;
  const [showModal, setShowModal] = useState(false);
  const [selectedExclusion, setSelectedExclusion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    props.getExclusions(customerId, true);
  }, []);

  const openNewExclusionModal = () => {
    setSelectedExclusion(null);
    setShowModal(true);
  };

  const hideExclusionModal = (rerender: boolean) => {
    setShowModal(false);
    setSelectedExclusion(null);
    if (rerender) {
      props.getExclusions(customerId, rerender);
    }
  };

  const openEditExclusion = (exclusion: IOrderExclusion) => {
    setSelectedExclusion(exclusion);
    setShowModal(true);
  };

  const deleteEditExclusion = async (exclusion: IOrderExclusion) => {
    if (confirm(locale.orderExclusions._deleteConfirm + exclusion.name)) {
      try {
        setIsLoading(true);
        await OrderExclusionService.deleteExclusion(
          exclusion.id,
          exclusion.customerId
        );
        Toastr.success(exclusion.name + locale.orderExclusions._deleteSuccess);
      } catch (error) {
        Toastr.error(error);
      } finally {
        setIsLoading(false);
        props.getExclusions(customerId, true);
      }
    }
  };

  return (
    <>
      <div className="order-exclusions-admin">
        <div className="order-exclusions-admin-header-row row">
          <PaneHeadline
            titleText={locale.orderExclusions._exclusionDays}
            titleIcon={faCalendarTimes}
            className="col-sm-6"
          />
          <div className="col-sm-6 text-right mb-3">
            <button
              className="btn btn-outline-dark"
              onClick={() => openNewExclusionModal()}
            >
              {locale.orderExclusions._newExclusion}
            </button>
          </div>
        </div>
        <Spinner2 show={props.isLoading || isLoading} />
        <div className="order-exclusions-content">
          <OrderExclusionsGrid
            data={props.exclusions}
            editExclusion={openEditExclusion}
            deleteExclusion={deleteEditExclusion}
          />
        </div>
        {showModal && (
          <OrderExclusionModal
            customerId={customerId}
            exclusion={selectedExclusion}
            onHide={hideExclusionModal}
            show={showModal}
          />
        )}
      </div>
    </>
  );
}
